import * as React from "react"
import { useState, useEffect, useCallback, useMemo } from "react"
import {
    Alert, Box, Grid, Card, Typography, Button, Chip, TextField, InputAdornment,
    ToggleButton, ToggleButtonGroup, Table, TableHead, TableRow, TableCell, TableBody,
    CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material"
import { IconSeeker } from "@ui-seeker/core"
import { useAppToast } from "@ui-seeker/app"
import { getSubmit, postSubmit } from "@ui-seeker/utils"

import { ALL_SITES_VALUE, AutocompleteAsyncDebounced, SiteSelect } from "@components"
import { BACKEND_APIS } from "@constants"
import BrandThumbnail, { withThumbnails } from "./BrandThumbnail"

const PB = BACKEND_APIS.PRODUCT_BRANDS
const idOf = (v) => (v && typeof v === 'object' ? v.value : v)

// Brand texts one Normalize run processes. Mirrors NORMALIZE_BATCH in the backend
// (app/services/brand_normalization.py), which is what actually enforces it — this copy
// only exists so the UI can name the number instead of saying "some".
const NORMALIZE_BATCH = 100

// Rows the listing shows, and the only page there is — this tab does not paginate.
// Same number as the batch on purpose: the rows on screen are the rows the button acts
// on. Paging over a set the button mutates reshuffles it, so page 2 would skip rows.
const PAGE_SIZE = NORMALIZE_BATCH

/**
 * Product brand normalization for one site (Puig).
 *
 * Raw brand text comes from LAST_RAW (ClickHouse) and the canonical id_brand is
 * written to DIPI_SKU_MAPPING, stamping qa = 1.
 *
 * qa = 1 means "the normalizer must not touch this row" — it does NOT lock the row
 * against an operator, who can always reassign. That stamp is what makes the fix
 * survive the next extraction; brand-new products scraped later still go through the
 * normalizer.
 */
export default function ProductBrandsPanel({ cid, site: selectedSite, onSiteChange }) {
    const { showToast } = useAppToast()

    // The WHOLE site catalog, fetched once per customer: the selector filters it in
    // the browser instead of asking the backend on every keystroke.
    const [siteOptions, setSiteOptions] = useState([])
    const [loadingSites, setLoadingSites] = useState(false)
    // Canonical brand filter. The ID and not the option object: AutocompleteAsyncSeeker
    // echoes whatever it is given back to the backend as `selected`, which int()s it —
    // handing it the object answered 500 on every keystroke.
    const [brandId, setBrandId] = useState(null)
    const [rows, setRows] = useState([])
    const [ignored, setIgnored] = useState(0)
    const [loading, setLoading] = useState(false)
    const [search, setSearch] = useState('')
    const [status, setStatus] = useState('all')       // same default as dipi
    const [endDate, setEndDate] = useState('')        // empty = no date filter, like dipi
    const [firstDate, setFirstDate] = useState('')    // products NEW since (first_date); empty = off
    // Totals of the WHOLE filtered set, sent by the backend: computing them from `rows`
    // would only ever describe the page on screen.
    const [total, setTotal] = useState(0)
    const [pendingProducts, setPendingProducts] = useState(0)
    const [assignTarget, setAssignTarget] = useState(null)  // {brand}
    const [assignBrand, setAssignBrand] = useState(null)
    const [savingAssign, setSavingAssign] = useState(false)
    const [running, setRunning] = useState(false)     // keyword normalize in flight

    // The site is picked on the page and shared with the Visibility tab, but the two tabs
    // read DIFFERENT catalogs (828 product sites against 348 visibility ones), so the
    // shared pick may name a site this tab has no data for — "All sites" among them, which
    // only visibility serves. Resolve it here and take the catalog's own object: the label
    // and the flag come from it. An unknown site leaves the box empty WITHOUT clearing the
    // shared value, so the other tab keeps its selection.
    const site = useMemo(
        () => (selectedSite ? siteOptions.find(o => o.value === selectedSite.value) || null : null),
        [selectedSite, siteOptions]
    )
    const siteId = idOf(site)
    // Only once the catalog is in: while it is on the wire nothing has resolved yet.
    const siteUnavailable = !!selectedSite && !site && !loadingSites && siteOptions.length > 0

    // The site list belongs to the customer, so a customer change invalidates it.
    useEffect(() => {
        setBrandId(null)
        if (!cid) { setSiteOptions([]); return }
        setLoadingSites(true)
        getSubmit(`${PB}/sites`, { customerID: cid, all: 1 })
            .then(res => setSiteOptions(res || []))
            .catch(() => { setSiteOptions([]); showToast.error('Could not load sites') })
            .finally(() => setLoadingSites(false))
    }, [cid])

    const load = useCallback(() => {
        if (!cid || !siteId) { setRows([]); setIgnored(0); setTotal(0); setPendingProducts(0); return }
        setLoading(true)
        const params = new URLSearchParams({
            customerID: cid, siteID: siteId,
            limit: PAGE_SIZE,
        })
        if (search.trim()) params.set('search', search.trim())
        if (status !== 'all') params.set('status', status)
        if (brandId) params.set('brandID', brandId)
        if (endDate) params.set('endDate', endDate)
        if (firstDate) params.set('firstDate', firstDate)
        getSubmit(`${PB}?${params.toString()}`)
            .then(res => {
                // Thumbnail URLs resolved once for the page, never per row.
                setRows(withThumbnails(res?.brands || [], 'sample_thumbnail'))
                setIgnored(res?.ignored_without_mapping || 0)
                setTotal(res?.total || 0)
                setPendingProducts(res?.pending_products || 0)
            })
            .catch(() => {
                showToast.error('Could not load product brands')
                setRows([]); setIgnored(0); setTotal(0); setPendingProducts(0)
            })
            .finally(() => setLoading(false))
    }, [cid, siteId, search, status, endDate, firstDate, brandId])

    useEffect(() => {
        const t = setTimeout(load, 300)
        return () => clearTimeout(t)
    }, [load])

    // Whether the toolbar is narrowing what the table shows. Drives both the scope sent
    // to the backend and the button's own label: the run used to cover the whole site
    // regardless of the filters, which reads as a bug when the button sits next to the
    // search box (with "dior" typed the header said 6 pending and the run wrote 397 rows).
    const isFiltered = !!(search.trim() || endDate || firstDate)

    // One string, shown above AND below the table. `total` here is exact: the listing
    // groups and counts in a single pass, with no lagging mirror in the way.
    const truncatedNotice = !loading && total > rows.length && rows.length
        ? `Showing ${rows.length} of ${total.toLocaleString()} brands`
          + ' — narrow the search to reach the rest.'
        : null

    // Brands with pending products AMONG THE ONES ON SCREEN — the set Normalize acts on,
    // now that the listing returns one page of PAGE_SIZE and the run reads the same
    // filters and the same batch size.
    const pendingHere = useMemo(() => rows.filter(r => r.pending > 0).length, [rows])

    // Products behind those rows. Summed from the rows on screen, NOT taken from the
    // header's `pendingProducts`, which counts the whole filtered set: with 100 of 772
    // brands listed that read "100 brands shown (78.315 products)" — a page-level count
    // next to a set-level one, describing a run that touches neither figure.
    const pendingProductsHere = useMemo(
        () => rows.reduce((n, r) => n + (r.pending || 0), 0), [rows])

    const runNormalize = async () => {
        if (!cid || !siteId) return
        setRunning(true)
        try {
            // Only sent when set: an absent filter means the whole site, the original
            // behaviour.
            const body = { id_customer: cid, id_site: siteId }
            if (search.trim()) body.search = search.trim()
            if (endDate) body.endDate = endDate
            if (firstDate) body.firstDate = firstDate
            const res = await postSubmit(`${PB}/normalize`, body)
            // Two sources now: the product's own SKU (a fact) and, only when it has no
            // SKU, the brand text through the matcher. Worth splitting in the toast —
            // they are not equally trustworthy.
            // The run is capped to a batch of brand texts, so the remainder is part of
            // the result: without it "Normalized 340 of 412" reads as finished.
            // `capped` means the BATCH cut this run, so there is a real queue behind it.
            const left = Math.max((res.pending_texts_total || 0) - (res.matched_texts || 0), 0)
            // Products actually written: the SKU path plus the text path.
            const wrote = (res.from_sku || 0) + (res.matched_products || 0)
            // Green says "done"; a run that resolved nothing is a report, not an
            // achievement. Same text, honest colour.
            showToast[wrote ? 'success' : 'info'](
                `Normalized ${res.from_sku + res.matched_products} of ${res.pending_products} pending products`
                + ` · ${res.from_sku} by SKU · ${res.matched_products} by brand text (${res.matched_texts} texts)`
                + ` · ambiguous ${res.ambiguous_texts} · unmatched ${res.unmatched_texts}`
                + (res.capped ? ` · ${left.toLocaleString()} brands still pending` : ''))
            // Only when something changed: with nothing written the table is already
            // correct, and the reload re-runs the whole LAST_RAW × SKU_MAPPING cross.
            if (wrote) load()
        } catch (e) {
            showToast.error('Error normalizing product brands')
        } finally {
            setRunning(false)
        }
    }

    // Confirmed like Clear, and for a stronger reason: this one writes MORE. Kept to the
    // count and the scope on purpose — the toast is narrow, and a paragraph nobody reads
    // guards nothing. HOW the brand is chosen lives in the button's tooltip.
    //
    // `pendingProducts` is exact here: the listing already reports the pending products
    // of the WHOLE filtered set, which is the run's scope. The one gap is the status
    // toggle, which the header honours and the run does not — on Normalized this reads 0
    // and refuses a run that had work to do. It errs on the side of doing nothing, so it
    // stays; the fix belongs in the toolbar, not in a second request.
    //
    // With nothing pending there is nothing to confirm: asking "normalize 0 products?
    // Cannot be undone" is noise, and noise is what teaches people to hit OK unread. Say
    // why instead — the run is a no-op, not a refusal.
    const confirmNormalize = () => {
        if (!pendingHere) {
            showToast.info(total
                ? 'Nothing pending among the rows shown — narrow the search to reach the rest.'
                : 'Nothing pending here — every product already has a brand.')
            return
        }
        // JSX, not a string: ConfirmContent renders `message` inside a Typography, so the
        // scope can carry the emphasis. "shown" is the word that bounds the whole action.
        showToast.confirm(
            <>
                Normalize the {pendingHere} pending brand{pendingHere === 1 ? '' : 's'}
                {' '}<strong>shown</strong> ({pendingProductsHere.toLocaleString()} products)?
                {' '}Cannot be undone.
            </>,
            { onSubmit: runNormalize })
    }

    // `brandArg` lets Clear act straight from a row without opening the dialog: reading
    // assignTarget right after setAssignTarget would still see the previous value (the
    // state update lands on the next render), and it is null on a fresh page.
    const doAssign = async (id_brand, brandArg) => {
        const brand = brandArg ?? assignTarget?.brand
        if (!brand) return
        setSavingAssign(true)
        try {
            const res = await postSubmit(`${PB}/assign`, {
                id_customer: cid, id_site: siteId, brand, id_brand,
            })
            showToast.success(
                id_brand ? `${res.updated} rows updated` : `${res.updated} rows cleared`)
            setAssignTarget(null); setAssignBrand(null)
            load()
        } catch (e) {
            showToast.error('Error assigning brand')
        } finally {
            setSavingAssign(false)
        }
    }

    return (
        <Grid container spacing={2}>
            {/* Toolbar */}
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    {/* The filters wrap among THEMSELVES. Keeping them in their own box
                        is what stops the counts and the Normalize button from being
                        pushed onto a line of their own once the brand filter joined the
                        row — they belong top right, where they were. */}
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center',
                               flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
                    <Box sx={{ minWidth: 260 }}>
                        {/* Async (server-side search), not Ajax: LAST_RAW has ~918 sites, and
                            the Ajax variant loads the whole list once and filters client-side.
                            queryParam="search" matches the backend's param (default is "qs"). */}
                        <SiteSelect
                            options={siteOptions}
                            value={site}
                            onChange={onSiteChange}
                            loading={loadingSites}
                        />
                    </Box>
                    {/* Only a resolved row can carry a canonical brand, so picking one
                        moves the toggle to Normalized. It does NOT disable the others:
                        "this brand + Not synced" — assigned here, not in the dashboards
                        yet — is a question worth asking (decision 2026-09-10). */}
                    <Box sx={{ minWidth: 240 }}>
                        <AutocompleteAsyncDebounced
                            urlAjax={`${BACKEND_APIS.BRANDS}/crud/brand/find-options`}
                            label="Canonical brand"
                            placeholder="All brands"
                            queryParam="search"
                            value={brandId}
                            onChange={(e, op) => {
                                setBrandId(e.target.value || null)
                                if (op?.value) setStatus('normalized')
                            }}
                        />
                    </Box>
                    <TextField size="small" placeholder="Search raw brand or product…" value={search}
                               onChange={e => setSearch(e.target.value)} sx={{ minWidth: 280 }}
                               InputProps={{ startAdornment: (
                                   <InputAdornment position="start"><IconSeeker icon="search" sx={{ color: '#9ca3af' }} /></InputAdornment>
                               ) }} />
                    <ToggleButtonGroup size="small" exclusive value={status}
                                       onChange={(e, v) => v && setStatus(v)}>
                        <ToggleButton value="all">All</ToggleButton>
                        <ToggleButton value="pending">Pending</ToggleButton>
                        <ToggleButton value="normalized">Normalized</ToggleButton>
                    </ToggleButtonGroup>
                    <TextField size="small" type="date" label="Seen since (end date)"
                               value={endDate} onChange={e => setEndDate(e.target.value)}
                               InputLabelProps={{ shrink: true }} sx={{ minWidth: 185 }}
                               inputProps={{ title: 'Only count products whose SKU_MAPPING end_date is on/after this date' }} />
                    <TextField size="small" type="date" label="New since (first date)"
                               value={firstDate} onChange={e => setFirstDate(e.target.value)}
                               InputLabelProps={{ shrink: true }} sx={{ minWidth: 185 }}
                               inputProps={{ title: 'Only count products whose SKU_MAPPING first_date is on/after this date (new since)' }} />
                    </Box>
                    <Box sx={{ flexShrink: 0, ml: 'auto' }}>
                    {/* No Tooltip around this Button: it toggles between disabled and
                        enabled with the site, and MUI's Tooltip renders a disabled child
                        differently — that swap desyncs the hook order ("Should have a
                        queue"). The hint lives in the button's own title. */}
                    {/* The label carries the CAP, which is the one thing about this button
                        that surprises people: it never does "everything", it does a batch.
                        Stated unconditionally rather than only when it bites — the counter
                        for pending TEXTS (the unit the cap is in) is not on screen, and a
                        limit you learn about from the result toast is a limit that already
                        misled you once. */}
                    <Button variant="contained" disabled={!siteId || running}
                            title={'Assign the canonical brand to the pending brands listed here,'
                                + " taking it from each product's SKU or, with no SKU, from the keyword"
                                + ' matcher. Ambiguous texts and products a QA validated are left alone.'}
                            startIcon={running ? <CircularProgress size={16} /> : <IconSeeker icon="wand-magic-sparkles" />}
                            onClick={confirmNormalize}>
                        Normalize (keywords)
                    </Button>
                    </Box>
                </Box>
                {/* Its own line under the toolbar, right-aligned. Inside the row — beside
                    the button or stacked under it — this text is wide enough that the
                    flex column inherits its width, which is what kept pushing Seen since
                    onto a line of its own. */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                    {siteId && !loading && (
                        <Typography variant="body2" color="text.secondary">
                            {total.toLocaleString()} brands · {pendingProducts.toLocaleString()} products pending
                        </Typography>
                    )}
                </Box>
            </Grid>

            {siteId && (
                <Grid item xs={12}>
                    <Alert severity="info" sx={{ py: 0.5 }}>
                        Reassigning writes the canonical brand on this site's products and marks
                        them validated, so <strong>the normalizer will not overwrite them</strong>
                        {' '}again. Products scraped later still go through the normalizer.
                        {ignored > 0 && (
                            <> · {ignored.toLocaleString()} raw products have no SKU mapping row and are not listed.</>
                        )}
                    </Alert>
                </Grid>
            )}

            {/* Same line as the one under the table. Repeated on purpose: the
                table is 100 rows tall, so a note only at the bottom is a note
                nobody reads before acting on what they can see. */}
            {truncatedNotice && (
                <Grid item xs={12} sx={{ pb: 0 }}>
                    <Typography variant="body2" color="text.secondary">
                        {truncatedNotice}
                    </Typography>
                </Grid>
            )}

            {/* Table */}
            <Grid item xs={12}>
                {!siteId ? (
                    <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                        <IconSeeker icon="tags" sx={{ fontSize: 48, mb: 1 }} />
                        {/* The site is shared with Visibility, whose catalog is not this one.
                            Saying so beats an empty box that looks like the filter was lost. */}
                        <Typography>
                            {!siteUnavailable
                                ? 'Select a site to review and fix its product brands.'
                                : selectedSite.value === ALL_SITES_VALUE
                                    ? '"All sites" only applies to Visibility — pick a site.'
                                    : `"${selectedSite.label}" has no product data for this customer — pick another site.`}
                        </Typography>
                    </Box>
                ) : (
                    <Card variant="outlined">
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
                        ) : rows.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>No brands found.</Box>
                        ) : (
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ width: 96 }} />
                                        <TableCell>Raw brand</TableCell>
                                        <TableCell>Example product</TableCell>
                                        <TableCell align="right">Products</TableCell>
                                        <TableCell align="right">Pending</TableCell>
                                        <TableCell>Canonical brand</TableCell>
                                        <TableCell align="center"
                                                   title="Products a QA has validated (qa = 1): the normalizer will not overwrite them">
                                            QA
                                        </TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map(r => (
                                        <TableRow key={r.brand} hover>
                                            {/* The most recently seen product of the group —
                                                same one the title comes from. */}
                                            <TableCell sx={{ width: 96, py: 0.5 }}>
                                                <BrandThumbnail src={r.thumbnail_url} alt={r.brand} />
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 500 }}>{r.brand}</TableCell>
                                            {/* One real product title per raw brand: what the QA
                                                reads to tell which brand the text actually is.
                                                Native `title`, not MUI's Tooltip: with up to 1.000
                                                rows, one Tooltip component per row is what made the
                                                Visibility tab blow up. */}
                                            <TableCell title={r.sample_title || ''}
                                                       sx={{ maxWidth: 320, overflow: 'hidden',
                                                             textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {r.sample_title || '—'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">{r.product_count.toLocaleString()}</TableCell>
                                            <TableCell align="right">
                                                {r.pending > 0 ? (
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'warning.main' }}>
                                                        {r.pending.toLocaleString()}
                                                    </Typography>
                                                ) : (
                                                    <Typography variant="body2" color="text.disabled">—</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {r.id_brand ? (
                                                    <Chip size="small" variant="outlined"
                                                          color={r.inconsistent ? 'warning' : (r.fully_normalized ? 'success' : 'default')}
                                                          label={r.inconsistent
                                                              ? `${r.canonical_name || r.id_brand} (mixed)`
                                                              : (r.canonical_name || r.id_brand)} />
                                                ) : (
                                                    <Chip size="small" variant="outlined" label="— pending" sx={{ color: 'text.disabled' }} />
                                                )}
                                            </TableCell>
                                            {/* Read-only. A row is a brand text covering many
                                                products, so this is a count, not a yes/no: an
                                                assign stamps them all, and products scraped
                                                later come in unflagged. Only what a QA
                                                validated is highlighted — "not reviewed yet" is
                                                the normal state of almost every row, not an
                                                error worth painting red. */}
                                            <TableCell align="center"
                                                       title={`${r.qa_products.toLocaleString()} of ${r.product_count.toLocaleString()} products validated by a QA`}>
                                                {r.qa_products === 0 ? (
                                                    <Typography variant="body2" color="text.disabled">—</Typography>
                                                ) : (
                                                    <Chip size="small" variant="outlined"
                                                          color={r.qa_products === r.product_count ? 'success' : 'warning'}
                                                          label={r.qa_products === r.product_count ? 'QA' : 'QA mixed'} />
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Button size="small"
                                                        onClick={() => { setAssignTarget({ brand: r.brand }); setAssignBrand(null) }}>
                                                    {r.id_brand ? 'Reassign' : 'Assign'}
                                                </Button>
                                                {/* Confirmed: the write has no undo — the previous
                                                    id_brand is not stored anywhere and this screen
                                                    does not go through DIPI_MAPPING_AUDIT. The count
                                                    is in the message on purpose: a row can cover
                                                    thousands of products, and that number is what
                                                    actually stops a misclick. */}
                                                {r.id_brand && (
                                                    <Button size="small" color="error"
                                                            onClick={() => showToast.confirm(
                                                                <>Clear "{r.brand}"?{' '}
                                                                <strong>{r.product_count.toLocaleString()} products</strong>
                                                                {' '}lose their brand. Cannot be undone.</>,
                                                                { onSubmit: () => doAssign(null, r.brand) })}>
                                                        Clear
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                        {/* No pager. The list is one page of PAGE_SIZE and that page is
                            exactly what Normalize acts on; paging over a set the button
                            mutates reshuffles it, so page 2 would skip rows. The header
                            carries the real total, so the tail is stated, not hidden. */}
                        {truncatedNotice && (
                            <Box sx={{ px: 2, py: 1, color: 'text.secondary' }}>
                                <Typography variant="body2">{truncatedNotice}</Typography>
                            </Box>
                        )}
                    </Card>
                )}
            </Grid>

            <Dialog open={!!assignTarget} onClose={() => setAssignTarget(null)} fullWidth maxWidth="sm">
                <DialogTitle>Normalize product brand</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Raw brand: <strong>{assignTarget?.brand}</strong>
                    </Typography>
                    <AutocompleteAsyncDebounced
                        urlAjax={`${BACKEND_APIS.BRANDS}/crud/brand/find-options`}
                        label="Canonical brand (DIPI_BRAND_NEW)"
                        placeholder="Search canonical brand…"
                        value={assignBrand}
                        onChange={e => setAssignBrand(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAssignTarget(null)}>Cancel</Button>
                    <Button variant="contained" disabled={!idOf(assignBrand) || savingAssign}
                            startIcon={savingAssign ? <CircularProgress size={16} /> : null}
                            onClick={() => doAssign(idOf(assignBrand))}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    )
}
